import React, { useEffect, useState } from "react";
import { useHospitals } from "../../hooks/useHospitals";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useSession } from "../../context/SessionContext";
import { Hospital, HospitalUpdatePayload } from "../../types/hospital.types";
import { capitalizeFullName, normalizeForCompare } from "../../lib/formatters";
import { Button, Input, notify } from "../ui";
import { SuggestInput } from "../patterns/SuggestInput";
import { t } from "../../i18n";
import HospitalEditModal from "./HospitalEditModal";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

interface HospitalLinkFormProps {
  // Hospitais já vinculados ao médico — usados para não sugerir de novo um
  // hospital que ele já tem, e para bloquear duplicidade ao criar um novo.
  existingHospitals: Hospital[];
  // Disparado depois que um hospital é vinculado com sucesso (novo ou já
  // existente).
  onLinked: (hospital: Hospital) => void;
  submitLabel?: string;
}

// Núcleo reutilizável do fluxo "buscar/cadastrar/vincular um hospital" —
// mesmo padrão de `CompanyLinkForm`, adaptado para os campos de hospital
// (aqui `email` é obrigatório e não existem `phone`/`cnpj`).
const HospitalLinkForm: React.FC<HospitalLinkFormProps> = ({
  existingHospitals,
  onLinked,
  submitLabel = t("links.hospital.submit"),
}) => {
  const { searchHospitals, createHospital, updateHospital, linkHospital, searching, creating, updating, linking } =
    useHospitals();
  const { setMetadata } = useSession();

  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [suggestions, setSuggestions] = useState<Hospital[]>([]);

  // Hospital clicado nas sugestões (já existe no sistema) — aguardando
  // confirmação de vínculo (e, opcionalmente, correção de dados) no modal.
  const [hospitalToReview, setHospitalToReview] = useState<Hospital | null>(null);

  const debouncedName = useDebouncedValue(nameInput, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    const query = debouncedName.trim();

    // `debouncedName` só reflete o texto atual depois que o delay termina.
    // Sem esta checagem, dispararíamos uma busca fantasma para um texto
    // que o usuário já apagou/trocou antes do debounce terminar.
    if (query !== nameInput.trim() || query.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    searchHospitals(query).then((result) => {
      if (!cancelled && result) setSuggestions(result);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedName]);

  const isAlreadyLinked = (hospitalId: number) => existingHospitals.some((h) => h.id === hospitalId);

  const handleNameBlur = () => {
    if (nameInput.trim()) {
      setNameInput(capitalizeFullName(nameInput.trim()));
    }
  };

  // Hospital já existe no sistema — em vez de vincular direto, abre o modal
  // de conferência/correção de dados (ver `handleReviewConfirm`).
  const handleSelectSuggestion = (hospital: Hospital) => {
    setSuggestions([]);
    setHospitalToReview(hospital);
  };

  const clearForm = () => {
    setNameInput("");
    setEmailInput("");
    setSuggestions([]);
  };

  const linkAndSync = async (hospital: Hospital) => {
    if (!(await linkHospital(hospital.id))) return false;

    // `metadata.hospitals` é lido em outras telas do perfil do médico — sem
    // atualizar aqui, essas telas ficariam com a lista desatualizada até o
    // médico deslogar e logar de novo.
    setMetadata((prev) => ({
      ...prev,
      hospitals: prev.hospitals.some((h) => h.id === hospital.id) ? prev.hospitals : [...prev.hospitals, hospital],
    }));
    onLinked(hospital);
    return true;
  };

  const handleAdd = async () => {
    const trimmedName = nameInput.trim();
    const trimmedEmail = emailInput.trim();

    if (!trimmedName || !trimmedEmail) {
      notify({ tone: "error", title: t("links.hospital.required") });
      return;
    }

    if (existingHospitals.some((h) => h.name.toLowerCase() === trimmedName.toLowerCase())) {
      notify({ tone: "error", title: t("links.hospital.alreadyLinked") });
      return;
    }

    const created = await createHospital({
      name:  capitalizeFullName(trimmedName),
      email: trimmedEmail.toLowerCase(),
    });
    // erro já foi mostrado via toast pelo useApi (ex: e-mail inválido)
    if (!created) return;

    if (await linkAndSync(created)) clearForm();
  };

  // Confirmação vinda do modal de revisão (clique numa sugestão já existente):
  // `payload` só tem os campos que o médico corrigiu — vazio quando ele optou
  // por "Apenas vincular" ou quando não alterou nada.
  const handleReviewConfirm = async (payload: HospitalUpdatePayload) => {
    if (!hospitalToReview) return;

    let hospitalToLink = hospitalToReview;

    if (Object.keys(payload).length > 0) {
      const updated = await updateHospital(hospitalToReview.id, payload);
      // erro (ex: e-mail inválido) já foi mostrado via toast — mantém o modal aberto para corrigir
      if (!updated) return;
      hospitalToLink = updated;
    }

    if (await linkAndSync(hospitalToLink)) setHospitalToReview(null);
  };

  const isSubmitting = creating || linking;

  // `suggestions` só é atualizado quando a busca debounced resolve — sem este
  // filtro, uma sugestão buscada para um texto anterior continuaria clicável
  // mesmo depois do médico já ter digitado algo que não bate mais com ela.
  const normalizedQuery = normalizeForCompare(nameInput.trim());
  const visibleSuggestions = suggestions.filter(
    (h) => !isAlreadyLinked(h.id) && normalizeForCompare(h.name).includes(normalizedQuery),
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      {hospitalToReview && (
        <HospitalEditModal
          hospital={hospitalToReview}
          saving={updating || linking}
          onConfirm={handleReviewConfirm}
          onClose={() => setHospitalToReview(null)}
        />
      )}

      <SuggestInput
        value={nameInput}
        onChange={setNameInput}
        onBlur={handleNameBlur}
        onEnter={handleAdd}
        placeholder={t("links.hospital.namePlaceholder")}
        disabled={isSubmitting}
        searching={searching}
        suggestions={visibleSuggestions}
        getKey={(hospital) => hospital.id}
        onSelect={handleSelectSuggestion}
        renderSuggestion={(hospital) => (
          <>
            <span className="font-medium">{hospital.name}</span>
            {hospital.email && <span className="block text-caption text-fg-subtle">{hospital.email}</span>}
          </>
        )}
      />

      <div className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-2">
        <Input
          type="email"
          aria-label={t("links.hospital.emailPlaceholder")}
          placeholder={t("links.hospital.emailPlaceholder")}
          value={emailInput}
          disabled={isSubmitting}
          onChange={(e) => setEmailInput(e.target.value.toLowerCase())}
        />
      </div>

      <Button
        variant="primary"
        className="self-start"
        onClick={handleAdd}
        loading={isSubmitting}
        disabled={!nameInput.trim() || !emailInput.trim()}
      >
        {submitLabel}
      </Button>
    </div>
  );
};

export default HospitalLinkForm;
